import axios from 'axios';

const API_BASE_URL = '/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for session-based auth
});

// Request interceptor for session-based auth
api.interceptors.request.use(
  (config) => {
    // Ensure withCredentials is always true for session-based auth
    config.withCredentials = true;
    
    // For browser environments, cookies should be sent automatically
    // For Node.js environments, we might need to handle cookies manually
    if (typeof window === 'undefined') {
      // Node.js environment - cookies need manual handling
      console.log('API Interceptor - Node.js environment detected');
    } else {
      // Browser environment - cookies should be automatic
      console.log('API Interceptor - Browser environment detected');
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle authentication errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    console.log('API Interceptor - Error:', error.response?.status, error.config?.url);
    
    // Handle network errors (backend down)
    if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
      console.log('API Interceptor - Network error, backend might be down');
      return Promise.reject(error);
    }
    
    // Handle timeout errors
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      console.log('API Interceptor - Request timeout');
      return Promise.reject(error);
    }
    
    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      // Don't redirect for /auth/me endpoint as it's used to check auth status
      const requestUrl = error.config?.url || '';
      console.log('API Interceptor - Checking URL:', requestUrl);
      
      if (requestUrl.includes('/auth/me') || requestUrl.endsWith('/me')) {
        console.log('API Interceptor - Skipping redirect for /auth/me endpoint');
        return Promise.reject(error);
      }
    }
    
    // Handle 500+ server errors
    if (error.response?.status >= 500) {
      console.log('API Interceptor - Server error:', error.response.status);
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: async (userData: any) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  getLinkedInPending: async () => {
    const response = await api.get('/auth/linkedin/pending');
    return response.data;
  },

  getLinkedInAuthUrl: (mode: 'register' | 'login') => `/api/auth/linkedin?mode=${mode}`,

  login: async (credentials: { email: string; password: string }) => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  adminLogin: async (credentials: { email: string; password: string }) => {
    const response = await api.post('/auth/admin/login', credentials);
    return response.data;
  },

  verifyOTP: async (email: string, otp: string) => {
    const response = await api.post('/auth/verify-otp', { email, otp });
    return response.data;
  },

  resendOTP: async (email: string) => {
    const response = await api.post('/auth/resend-otp', { email });
    return response.data;
  },

  logout: async () => {
    const response = await api.post('/auth/logout');
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  checkAuth: async () => {
    // Use the same endpoint as getCurrentUser since backend doesn't have /auth/check
    const response = await api.get('/auth/me');
    return response.data;
  },

  resendOTPAuthenticated: async () => {
    const response = await api.post('/auth/resend-otp-authenticated');
    return response.data;
  },

  // Forgot password functionality
  forgotPassword: async (email: string) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  verifyResetOTP: async (email: string, otp: string) => {
    const response = await api.post('/auth/verify-reset-otp', { email, otp });
    return response.data;
  },

  resetPassword: async (email: string, resetToken: string, newPassword: string) => {
    const response = await api.post('/auth/reset-password', { email, resetToken, newPassword });
    return response.data;
  },

  // ✅ NEW: Verify email only (for unverified users)
  verifyEmailOnly: async (email: string, otp: string) => {
    const response = await api.post('/auth/verify-email-only', { email, otp });
    return response.data;
  },

  // ✅ NEW: Email change functionality (unverified users only)
  requestEmailChange: async (newEmail: string) => {
    const response = await api.post('/auth/request-email-change', { newEmail });
    return response.data;
  },

  verifyEmailChange: async (otp: string) => {
    const response = await api.post('/auth/verify-email-change', { otp });
    return response.data;
  },

  cancelEmailChange: async () => {
    const response = await api.post('/auth/cancel-email-change');
    return response.data;
  }
};

// Profile API
export const profileAPI = {
  getProfile: async () => {
    const response = await api.get('/profile');
    return response.data;
  },

  updateProfile: async (data: any) => {
    const response = await api.put('/profile', data);
    return response.data;
  },

  uploadProfileImage: async (file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    const response = await api.post('/profile/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  uploadResume: async (file: File) => {
    const formData = new FormData();
    formData.append('resume', file);
    const response = await api.post('/profile/upload-resume', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  deleteResume: async () => {
    const response = await api.delete('/profile/resume');
    return response.data;
  },

  deleteProfileImage: async () => {
    const response = await api.delete('/profile/image');
    return response.data;
  },

  getProfileCompletion: async () => {
    const response = await api.get('/profile/completion');
    return response.data;
  },

  getPublicProfile: async (userId: string) => {
    const response = await api.get(`/profile/public/${userId}`);
    return response.data;
  },

  searchProfiles: async (filters?: {
    role?: string;
    skills?: string[];
    location?: string;
    experience?: number;
    availability?: string[];
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => {
    const response = await api.get('/profile/search', { params: filters });
    return response.data;
  },

  getProfileStats: async () => {
    const response = await api.get('/profile/stats');
    return response.data;
  },

  getSignedUrl: async (s3Key: string) => {
    // Extract filename from S3 URL/key
    const filename = s3Key.split('/').pop();
    const response = await api.get(`/upload/file/${filename}`);
    return response.data.url;
  },
};

export const resumeAnalysisAPI = {
  getStatus: async () => {
    const response = await api.get('/resume-analysis');
    return response.data;
  },

  analyze: async (jdText?: string) => {
    const response = await api.post('/resume-analysis/analyze', {
      jdText: jdText || undefined,
    });
    return response.data;
  },

  preview: async (file: File) => {
    const formData = new FormData();
    formData.append('resume', file);
    const response = await api.post('/resume-analysis/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

// Dashboard API
export const dashboardAPI = {
  // Get role-specific dashboard data
  getAspirantDashboard: async () => {
    const response = await api.get('/dashboard/aspirant');
    return response.data;
  },

  getMentorDashboard: async () => {
    const response = await api.get('/dashboard/mentor');
    return response.data;
  },

  getAdminDashboard: async () => {
    const response = await api.get('/dashboard/admin');
    return response.data;
  },

  // Admin users management methods
  getUsers: async (filters?: {
    role?: string;
    status?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) => {
    // Use admin endpoint for user management
    const response = await api.get('/admin/users', { params: filters });
    return response.data;
  },

  getPendingUsers: async () => {
    // Use dedicated pending users endpoint
    const response = await api.get('/admin/users/pending');
    return response.data;
  },

  approveUser: async (userId: string) => {
    const response = await api.post(`/admin/users/${userId}/approve`);
    return response.data;
  },

  rejectUser: async (userId: string, reason: string) => {
    const response = await api.post(`/admin/users/${userId}/reject`, { reason });
    return response.data;
  },

  deleteUser: async (userId: string) => {
    const response = await api.delete(`/admin/users/${userId}`);
    return response.data;
  },

  toggleUserStatus: async (userId: string) => {
    const response = await api.post(`/admin/users/${userId}/toggle-status`);
    return response.data;
  },

  // Remove non-existent endpoints
  // getStats and getActivities don't exist in backend
};

// Users API
export const usersAPI = {
  getAllUsers: async () => {
    const response = await api.get('/users');
    return response.data;
  },

  getMentors: async (params?: { page?: number; limit?: number }) => {
    const response = await api.get('/users/mentors', { params });
    return response.data;
  },

  getPendingUsers: async () => {
    const response = await api.get('/users/pending');
    return response.data;
  },

  approveUser: async (userId: string) => {
    const response = await api.put(`/users/${userId}/approve`);
    return response.data;
  },

  rejectUser: async (userId: string) => {
    const response = await api.put(`/users/${userId}/reject`);
    return response.data;
  },

  deleteUser: async (userId: string) => {
    const response = await api.delete(`/users/${userId}`);
    return response.data;
  },
};

// Mentorship API
export const mentorshipAPI = {
  // Send mentorship request
  sendRequest: async (mentorId: string, message: string) => {
    const response = await api.post('/mentorship/request', { mentorId, message });
    return response.data;
  },
  
  // Get requests for mentor
  getMentorRequests: async (status = 'all', page = 1, limit = 10) => {
    const response = await api.get(`/mentorship/requests/mentor?status=${status}&page=${page}&limit=${limit}`);
    return response.data;
  },
  
  // Get requests for aspirant
  getAspirantRequests: async (status = 'all', page = 1, limit = 10) => {
    const response = await api.get(`/mentorship/requests/aspirant?status=${status}&page=${page}&limit=${limit}`);
    return response.data;
  },
  
  // Respond to request (mentor)
  respondToRequest: async (requestId: string, action: 'accept' | 'reject', mentorNotes?: string) => {
    const response = await api.put(`/mentorship/requests/${requestId}/respond`, { action, mentorNotes });
    return response.data;
  },
  
  // Disconnect mentorship
  disconnect: async (requestId: string) => {
    const response = await api.put(`/mentorship/requests/${requestId}/disconnect`);
    return response.data;
  },
  
  // Get mentorship stats
  getStats: async () => {
    const response = await api.get('/mentorship/stats');
    return response.data;
  }
};

// Events API
export const eventsAPI = {
  getAllEvents: async () => {
    const response = await api.get('/events');
    return response.data;
  },

  getEvent: async (eventId: string) => {
    const response = await api.get(`/events/${eventId}`);
    return response.data;
  },

  getCarouselEvents: async () => {
    const response = await api.get('/events/carousel/home');
    return response.data;
  },

  createEvent: async (eventData: any) => {
    const response = await api.post('/events', eventData);
    return response.data;
  },

  updateEvent: async (eventId: string, eventData: any) => {
    const response = await api.put(`/events/${eventId}`, eventData);
    return response.data;
  },

  deleteEvent: async (eventId: string) => {
    const response = await api.delete(`/events/${eventId}`);
    return response.data;
  },

  approveEvent: async (eventId: string) => {
    const response = await api.put(`/events/${eventId}/approve`);
    return response.data;
  },

  rejectEvent: async (eventId: string) => {
    const response = await api.put(`/events/${eventId}/reject`);
    return response.data;
  },

  registerForEvent: async (eventId: string) => {
    const response = await api.post(`/events/${eventId}/register`);
    return response.data;
  },

  unregisterFromEvent: async (eventId: string) => {
    const response = await api.delete(`/events/${eventId}/register`);
    return response.data;
  },

  getEventRegistrations: async (eventId: string) => {
    const response = await api.get(`/events/${eventId}/registrations`);
    return response.data;
  },
};

// File Upload API
export const fileAPI = {
  uploadFile: async (file: File, category: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    
    const response = await api.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteFile: async (fileId: string) => {
    const response = await api.delete(`/files/${fileId}`);
    return response.data;
  },

  getFile: async (fileId: string) => {
    const response = await api.get(`/files/${fileId}`);
    return response.data;
  },
};

// Upload API
export const uploadAPI = {
  uploadResume: async (file: File) => {
    const formData = new FormData();
    formData.append('resume', file);
    const response = await api.post('/upload/resume', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  uploadProfileImage: async (file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    const response = await api.post('/upload/profile-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  
  uploadSpeakerPicture: async (file: File) => {
    const formData = new FormData();
    formData.append('speakerPicture', file);
    const response = await api.post('/upload/speaker-picture', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  uploadEventFlyer: async (file: File) => {
    const formData = new FormData();
    formData.append('eventFlyer', file);
    const response = await api.post('/upload/event-flyer', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  uploadSubmissionPresentation: async (file: File) => {
    const formData = new FormData();
    formData.append('presentation', file);
    const response = await api.post('/upload/submission-presentation', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  uploadSubmissionDocument: async (file: File) => {
    const formData = new FormData();
    formData.append('document', file);
    const response = await api.post('/upload/submission-document', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  downloadSubmissionFile: async (fileKey: string) => {
    const response = await api.get(`/upload/download-submission-file/${encodeURIComponent(fileKey)}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  testS3Connection: async () => {
    const response = await api.get('/upload/test-s3');
    return response.data;
  },

  verifyFileExists: async (fileKey: string) => {
    const response = await api.get(`/upload/verify-file/${encodeURIComponent(fileKey)}`);
    return response.data;
  },

  listRecentUploads: async () => {
    const response = await api.get('/upload/list-recent');
    return response.data;
  }
};

// Job API
export const jobAPI = {
  // Create a new job posting
  createJob: async (jobData: any) => {
    const response = await api.post('/jobs', jobData);
    return response.data;
  },

  // Get all approved and active jobs (public)
  getJobs: async (params?: {
    page?: number;
    limit?: number;
    category?: string;
    location?: string;
    experienceLevel?: string;
    employmentType?: string;
    search?: string;
  }) => {
    const response = await api.get('/jobs', { params });
    return response.data;
  },

  // Get job details by ID
  getJobById: async (jobId: string) => {
    const response = await api.get(`/jobs/${jobId}`);
    return response.data;
  },

  // Get jobs posted by current user
  getMyJobs: async () => {
    const response = await api.get('/jobs/my/jobs');
    return response.data;
  },

  // Update job (only by owner and before approval)
  updateJob: async (jobId: string, jobData: any) => {
    const response = await api.put(`/jobs/${jobId}`, jobData);
    return response.data;
  },

  // Delete job (only by owner and before approval)
  deleteJob: async (jobId: string) => {
    const response = await api.delete(`/jobs/${jobId}`);
    return response.data;
  }
};

// Admin Job API
export const adminJobAPI = {
  // Get all jobs for admin review
  getJobs: async (params?: {
    status?: string;
    page?: number;
    limit?: number;
  }) => {
    console.log('Calling admin jobs API with params:', params);
    const response = await api.get('/admin/jobs', { params });
    console.log('Admin jobs API response:', response.data);
    return response.data;
  },

  // Get job details for admin
  getJobById: async (jobId: string) => {
    const response = await api.get(`/admin/jobs/${jobId}`);
    return response.data;
  },

  // Approve job
  approveJob: async (jobId: string) => {
    const response = await api.put(`/admin/jobs/${jobId}/approve`);
    return response.data;
  },

  // Reject job
  rejectJob: async (jobId: string, reason?: string) => {
    const response = await api.put(`/admin/jobs/${jobId}/reject`, { reason });
    return response.data;
  },

  // Toggle job active status
  toggleJobActive: async (jobId: string) => {
    const response = await api.put(`/admin/jobs/${jobId}/toggle-active`);
    return response.data;
  },

  // Delete job (admin only)
  deleteJob: async (jobId: string) => {
    const response = await api.delete(`/admin/jobs/${jobId}`);
    return response.data;
  }
};

// Bug Report API
export const bugReportAPI = {
  // Create a new bug report
  createBugReport: async (bugData: any) => {
    const response = await api.post('/bug-reports', bugData);
    return response.data;
  },

  // Get user's own bug reports
  getMyBugReports: async () => {
    const response = await api.get('/bug-reports/my-reports');
    return response.data;
  },

  // Get bug report by ID
  getBugReportById: async (bugId: string) => {
    const response = await api.get(`/bug-reports/${bugId}`);
    return response.data;
  }
};

// Admin Bug Report API
export const adminBugReportAPI = {
  // Get all bug reports for admin
  getBugReports: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    module?: string;
    priority?: string;
    search?: string;
  }) => {
    const response = await api.get('/bug-reports/admin', { params });
    return response.data;
  },

  // Get bug report statistics
  getBugReportStats: async () => {
    const response = await api.get('/bug-reports/stats');
    return response.data;
  },

  // Update bug report status
  updateBugReportStatus: async (bugId: string, statusData: {
    status: string;
    adminNotes?: string;
    assignedTo?: string;
  }) => {
    const response = await api.put(`/bug-reports/${bugId}/status`, statusData);
    return response.data;
  },

  // Delete bug report
  deleteBugReport: async (bugId: string) => {
    const response = await api.delete(`/bug-reports/${bugId}`);
    return response.data;
  }
};

// Admin Payment Management API
export const adminPaymentAPI = {
  // Mark user as paid for hackathon registration
  markUserAsPaid: async (userId: string, hackathonId: string) => {
    const response = await api.post(`/admin/users/${userId}/mark-paid/${hackathonId}`);
    return response.data;
  },

  // Revert payment and registration for hackathon
  revertPayment: async (userId: string, hackathonId: string) => {
    const response = await api.post(`/admin/users/${userId}/revert-payment/${hackathonId}`);
    return response.data;
  },

  // Get all payments for a specific hackathon
  getHackathonPayments: async (hackathonId: string) => {
    const response = await api.get(`/admin/hackathons/${hackathonId}/payments`);
    return response.data;
  }
};

// Hackathon API
export const hackathonAPI: {
  getHackathons: (params?: {
    status?: string;
    page?: number;
    limit?: number;
    search?: string;
  }) => Promise<any>;
  getCarouselHackathons: () => Promise<any>;
  getHackathonById: (id: string) => Promise<any>;
  createHackathon: (hackathonData: any) => Promise<any>;
  updateHackathon: (id: string, hackathonData: any) => Promise<any>;
  deleteHackathon: (id: string) => Promise<any>;
  getHackathonStats: () => Promise<any>;
  assignMentors: (hackathonId: string, mentorIds: string[]) => Promise<any>;
  assignJudges: (hackathonId: string, judgeIds: string[]) => Promise<any>;
  cancelHackathon: (hackathonId: string) => Promise<any>;
  syncTeamCount: (hackathonId: string) => Promise<any>;
  getHackathonDetailedStats: (hackathonId: string) => Promise<any>;
} = {
  // Get all hackathons with pagination and filters
  getHackathons: async (params?: {
    status?: string;
    page?: number;
    limit?: number;
    search?: string;
  }) => {
    const response = await api.get('/hackathons', { params });
    return response.data;
  },

  // Get hackathons for home page carousel
  getCarouselHackathons: async () => {
    const response = await api.get('/hackathons/carousel/home');
    return response.data;
  },

  // Get hackathon by ID
  getHackathonById: async (id: string) => {
    const response = await api.get(`/hackathons/${id}`);
    return response.data;
  },

  // Create hackathon (Admin only)
  createHackathon: async (hackathonData: any) => {
    const response = await api.post('/hackathons', hackathonData);
    return response.data;
  },

  // Update hackathon (Admin only)
  updateHackathon: async (id: string, hackathonData: any) => {
    const response = await api.put(`/hackathons/${id}`, hackathonData);
    return response.data;
  },

  // Delete hackathon (Admin only) - Soft delete
  deleteHackathon: async (id: string) => {
    const response = await api.delete(`/hackathons/${id}`);
    return response.data;
  },

  // Get hackathon statistics (Admin only)
  getHackathonStats: async () => {
    const response = await api.get('/hackathons/stats/overview');
    return response.data;
  },

  // Assign mentors to hackathon (Admin only)
  assignMentors: async (hackathonId: string, mentorIds: string[]) => {
    const response = await api.post(`/hackathons/${hackathonId}/mentors`, { mentorIds });
    return response.data;
  },

  // Assign judges to hackathon (Admin only)
  assignJudges: async (hackathonId: string, judgeIds: string[]) => {
    const response = await api.post(`/hackathons/${hackathonId}/judges`, { judgeIds });
    return response.data;
  },

  // Cancel hackathon (Admin only) - Destroys all teams
  cancelHackathon: async (hackathonId: string) => {
    const response = await api.post(`/hackathons/${hackathonId}/cancel`);
    return response.data;
  },

  // Sync team count for hackathon (Admin only)
  syncTeamCount: async (hackathonId: string) => {
    const response = await api.post(`/hackathons/${hackathonId}/sync-team-count`);
    return response.data;
  },

  // Get detailed hackathon statistics including team counts
  getHackathonDetailedStats: async (hackathonId: string) => {
    const response = await api.get(`/hackathons/${hackathonId}/stats`);
    return response.data;
  }
};

// Registration API
export type RsvpResponse = 'going' | 'maybe' | 'not_going';

export const registrationAPI = {
  // RSVP to an event that uses attendanceMode 'rsvp'
  rsvpToEvent: async (eventId: string, response: RsvpResponse) => {
    const res = await api.post(`/registrations/event/${eventId}/rsvp`, { response });
    return res.data;
  },

  // The signed-in member's current RSVP for an event
  getMyRsvp: async (eventId: string) => {
    const res = await api.get(`/registrations/event/${eventId}/rsvp`);
    return res.data;
  },

  // The signed-in member's QR pass for an event
  getCheckInPass: async (eventId: string) => {
    const res = await api.get(`/registrations/event/${eventId}/check-in-pass`);
    return res.data;
  },

  // Admin marks attendance from a scanned check-in code
  checkInByToken: async (token: string) => {
    const res = await api.post(`/registrations/check-in/${token}`);
    return res.data;
  },

  // Register for an event (simplified - only optional fields)
  registerForEvent: async (eventId: string, data?: {
    notes?: string;
  }) => {
    const response = await api.post(`/registrations/event/${eventId}`, data || {});
    return response.data;
  },

  // Check if email is available for guest registration
  checkEmailAvailability: async (eventId: string, email: string) => {
    const response = await api.get(`/registrations/event/${eventId}/check-email/${encodeURIComponent(email)}`);
    return response.data;
  },

  // Register for an event as guest (no authentication required)
  registerForEventAsGuest: async (eventId: string, guestData: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    organization?: string;
    notes?: string;
    customFields?: { [key: string]: string };
  }) => {
    const response = await api.post(`/registrations/event/${eventId}/guest`, guestData);
    return response.data;
  },

  // Register for a hackathon
  registerForHackathon: async (hackathonId: string, data: any) => {
    const response = await api.post(`/registrations/hackathon/${hackathonId}`, data);
    return response.data;
  },

  // Get user's registrations
  getMyRegistrations: async () => {
    const response = await api.get('/registrations/my-registrations');
    return response.data;
  },

  // Get users looking for teams (hackathon)
  getUsersLookingForTeam: async (hackathonId: string) => {
    const response = await api.get(`/registrations/hackathon/${hackathonId}/looking-for-team`);
    return response.data;
  },

  // Get registrations for a hackathon (Admin only)
  getHackathonRegistrations: async (hackathonId: string) => {
    const response = await api.get(`/registrations/hackathon/${hackathonId}`);
    return response.data;
  },

  // Confirm registration (Admin only)
  confirmRegistration: async (registrationId: string) => {
    const response = await api.post(`/registrations/${registrationId}/confirm`);
    return response.data;
  },

  // Mark as attended
  markAttended: async (registrationId: string) => {
    const response = await api.post(`/registrations/${registrationId}/attend`);
    return response.data;
  },

  // Mark as attended (Admin only)
  markAttendedAdmin: async (registrationId: string) => {
    const response = await api.post(`/admin/registrations/${registrationId}/mark-attended`);
    return response.data;
  },

  // Withdraw registration
  withdrawRegistration: async (registrationId: string) => {
    const response = await api.post(`/registrations/${registrationId}/withdraw`);
    return response.data;
  },

  // Unregister from hackathon
  unregisterFromHackathon: async (hackathonId: string, leaveTeam: boolean = true) => {
    const response = await api.delete(`/registrations/hackathon/${hackathonId}/unregister`, {
      data: { leaveTeam }
    });
    return response.data;
  },

  // Get all registrations (Admin only)
  getAllRegistrations: async () => {
    const response = await api.get('/registrations/admin/all');
    return response.data;
  },

  // Get registration statistics (Admin only)
  getRegistrationStats: async () => {
    const response = await api.get('/registrations/admin/stats');
    return response.data;
  },

  // Get all hackathon registrations with team details (Admin only)
  getHackathonMembers: async (params?: {
    hackathonId?: string;
    status?: string;
    search?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.hackathonId) queryParams.append('hackathonId', params.hackathonId);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.search) queryParams.append('search', params.search);
    
    const response = await api.get(`/registrations/admin/hackathon-members?${queryParams.toString()}`);
    return response.data;
  }
};

// Team API
export const teamAPI = {
  // Create team
  createTeam: async (teamData: any) => {
    const response = await api.post('/teams', teamData);
    return response.data;
  },

  // Join team by code
  joinTeam: async (joinCode: string) => {
    const response = await api.post(`/teams/join/${joinCode}`);
    return response.data;
  },

  // Get team by ID
  getTeamById: async (id: string) => {
    const response = await api.get(`/teams/${id}`);
    return response.data;
  },

  // Update team (only leader can update)
  updateTeam: async (id: string, teamData: any) => {
    const response = await api.put(`/teams/${id}`, teamData);
    return response.data;
  },

  // Update team name (Admin only)
  updateTeamName: async (id: string, name: string) => {
    const response = await api.put(`/teams/${id}/name`, { name });
    return response.data;
  },

  // Submit project (only leader can submit)
  submitProject: async (id: string, submissionData: any) => {
    const response = await api.post(`/teams/${id}/submit`, submissionData);
    return response.data;
  },

  // Submit team for approval (Team leader only)
  submitForApproval: async (id: string) => {
    const response = await api.post(`/teams/${id}/submit-for-approval`);
    return response.data;
  },

  // Approve team (Admin only)
  approveTeam: async (id: string) => {
    const response = await api.post(`/teams/${id}/approve`);
    return response.data;
  },

  // Reject team (Admin only)
  rejectTeam: async (id: string, reason: string) => {
    const response = await api.post(`/teams/${id}/reject`, { reason });
    return response.data;
  },

  // Assign problem statement to team (Admin only)
  assignProblemStatement: async (id: string, problemStatement: any) => {
    const response = await api.post(`/teams/${id}/assign-problem`, { problemStatement });
    return response.data;
  },

  // Simple scoring
  scoreSubmission: async (id: string, score: number, feedback?: string) => {
    const response = await api.post(`/teams/${id}/score`, { score, feedback });
    return response.data;
  },

  // Remove member from team (only leader can remove members)
  removeMember: async (teamId: string, userId: string) => {
    const response = await api.delete(`/teams/${teamId}/members/${userId}`);
    return response.data;
  },

  // Withdraw a shortlist entirely (admin)
  clearOfferedProblemStatements: async (teamId: string) => {
    const response = await api.delete(`/teams/${teamId}/offered-problems`);
    return response.data;
  },

  // Withdraw the problem a team was given (admin)
  clearAssignedProblemStatement: async (teamId: string) => {
    const response = await api.delete(`/teams/${teamId}/assigned-problem`);
    return response.data;
  },

  // Undo a lock, so a team can be given a different problem (admin)
  unlockProblemStatement: async (teamId: string) => {
    const response = await api.post(`/teams/${teamId}/unlock-problem`);
    return response.data;
  },

  // Offer a team the shortlist it may choose from (admin)
  offerProblemStatements: async (teamId: string, statementIds: string[]) => {
    const response = await api.post(`/teams/${teamId}/offer-problems`, { statementIds });
    return response.data;
  },

  // Hand leadership to another member of the same team (admin)
  setTeamLeader: async (teamId: string, userId: string) => {
    const response = await api.put(`/teams/${teamId}/leader`, { userId });
    return response.data;
  },

  // Leave team (any member can leave)
  leaveTeam: async (teamId: string) => {
    const response = await api.post(`/teams/${teamId}/leave`);
    return response.data;
  },

  // Get team by join code
  getTeamByJoinCode: async (joinCode: string) => {
    const response = await api.get(`/teams/join/${joinCode}`);
    return response.data;
  },

  // Regenerate join code (Team leader only)
  regenerateJoinCode: async (teamId: string) => {
    const response = await api.post(`/teams/${teamId}/regenerate-join-code`);
    return response.data;
  },

  // Assign mentor to team (Admin only)
  assignMentor: async (teamId: string, mentorId: string) => {
    const response = await api.post(`/teams/${teamId}/assign-mentor`, { mentorId });
    return response.data;
  },

  // Remove mentor from team (Admin only)
  removeMentor: async (teamId: string) => {
    const response = await api.delete(`/teams/${teamId}/mentor`);
    return response.data;
  },

  // Toggle resubmission permission (Admin only)
  toggleResubmission: async (teamId: string) => {
    const response = await api.post(`/teams/${teamId}/toggle-resubmission`);
    return response.data;
  },

  // Get teams assigned to mentor
  getAssignedTeams: async () => {
    const response = await api.get('/teams/mentor/assigned');
    return response.data;
  },

  // Get user's teams (for aspirants)
  getMyTeams: async () => {
    const response = await api.get('/teams/my-teams');
    return response.data;
  },

  // Browse teams in a hackathon (for aspirants)
  browseHackathonTeams: async (hackathonId: string, filters?: any) => {
    const params = new URLSearchParams();
    if (filters) {
      // Only include non-empty values
      if (filters.search) params.append('search', filters.search);
      if (filters.filterType) params.append('filterType', filters.filterType);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());
    }
    const response = await api.get(`/teams/hackathon/${hackathonId}/browse?${params.toString()}`);
    return response.data;
  },

  // Apply to team
  applyToTeam: async (teamId: string, notes?: string) => {
    const response = await api.post(`/teams/${teamId}/apply`, { notes });
    return response.data;
  },

  // Accept application (Team leader only)
  acceptApplication: async (teamId: string, userId: string) => {
    const response = await api.post(`/teams/${teamId}/applications/${userId}/accept`);
    return response.data;
  },

  // Reject application (Team leader only)
  rejectApplication: async (teamId: string, userId: string, reason?: string) => {
    const response = await api.post(`/teams/${teamId}/applications/${userId}/reject`, { reason });
    return response.data;
  },

  // Withdraw application
  withdrawApplication: async (teamId: string) => {
    const response = await api.post(`/teams/${teamId}/applications/withdraw`);
    return response.data;
  },

  // Update team notes (Team leader only)
  updateTeamNotes: async (teamId: string, teamNotes?: string, isLookingForMembers?: boolean) => {
    const response = await api.put(`/teams/${teamId}/notes`, { teamNotes, isLookingForMembers });
    return response.data;
  },

  // Get user's applications
  getMyApplications: async () => {
    const response = await api.get('/teams/applications/my');
    return response.data;
  },

  // Get all teams (Admin only)
  getAllTeams: async () => {
    const response = await api.get('/teams');
    return response.data;
  },

  // Get available mentors for assignment
  getAvailableMentors: async () => {
    const response = await api.get('/users/mentors?limit=1000');
    return response.data;
  },

  // Get problem statements for a hackathon
  getHackathonProblemStatements: async (hackathonId: string) => {
    const response = await api.get(`/hackathons/${hackathonId}`);
    return response.data.problemStatements || [];
  },

  // Delete team (Admin can delete any team, Team leader can delete their own team if rejected or forming)
  deleteTeam: async (teamId: string) => {
    const response = await api.delete(`/teams/${teamId}`);
    return response.data;
  },

  // Deactivate team (Team leader or Admin only)
  deactivateTeam: async (teamId: string) => {
    const response = await api.post(`/teams/${teamId}/deactivate`);
    return response.data;
  },

  // Activate team (Team leader or Admin only)
  activateTeam: async (teamId: string) => {
    const response = await api.post(`/teams/${teamId}/activate`);
    return response.data;
  },

  // Get team assignment statistics
  getTeamAssignmentStats: async () => {
    const response = await api.get('/teams');
    const teams = response.data;
    
    // Calculate assignment counts
    const problemStatementCounts: { [key: string]: number } = {};
    const mentorCounts: { [key: string]: number } = {};
    
    teams.forEach((team: any) => {
      // Count problem statement assignments
      if (team.assignedProblemStatement?._id) {
        const psId = team.assignedProblemStatement._id;
        problemStatementCounts[psId] = (problemStatementCounts[psId] || 0) + 1;
      }
      
      // Count mentor assignments
      if (team.assignedMentor?._id) {
        const mentorId = team.assignedMentor._id;
        mentorCounts[mentorId] = (mentorCounts[mentorId] || 0) + 1;
      }
    });
    
    return {
      problemStatementCounts,
      mentorCounts
    };
  },

  // Get available members for a team (Team leader or Admin)
  getAvailableMembers: async (teamId: string, page = 1, limit = 20) => {
    const response = await api.get(`/teams/${teamId}/available-members?page=${page}&limit=${limit}`);
    return response.data;
  },

  // Add member to team (Team leader or Admin)
  addMemberToTeam: async (teamId: string, userId: string) => {
    const response = await api.post(`/teams/${teamId}/add-member/${userId}`);
    return response.data;
  }
};

export const mentorAPI = {
  getFeatured: async () => {
    const response = await api.get('/featured-mentors');
    return response.data;
  },
  
  getProfileImageSignedUrl: async (s3Key: string) => {
    // Extract the S3 key from the full URL if needed
    let key = s3Key;
    if (s3Key.startsWith('https://')) {
      // If it's a full S3 URL, extract just the key part
      const urlParts = s3Key.split('.com/');
      key = urlParts[1] || s3Key;
    }
    
    const response = await api.get(`/mentor-image-url?key=${encodeURIComponent(key)}`);
    return response.data.url;
  }
};

export default api; 